
import React, { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSubscriptions } from './hooks/useSubscriptions';
import { useCourierInfo } from './hooks/useCourierInfo';
import { AuthRequiredMessage } from './components/subscriptions/AuthRequiredMessage';
import { ErrorDisplay } from './components/subscriptions/ErrorDisplay';
import { SubscriptionsList } from './components/subscriptions/SubscriptionsList';
import { Dialog, DialogTitle, DialogDescription, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courierId, loading: courierLoading, error: courierError } = useCourierInfo();
  const { 
    subscriptions, 
    loading, 
    error, 
    actionLoading, 
    handleStatusChange,
    handleRefresh 
  } = useSubscriptions();

  // При монтировании компонента записываем ID курьера в консоль
  useEffect(() => {
    console.log('SubscriptionsPage - courierId:', courierId);
    console.log('SubscriptionsPage - userId:', user?.id);
    console.log('SubscriptionsPage - загруженные подписки:', subscriptions);
    console.log('SubscriptionsPage - состояния загрузки:', { 
      courierLoading, 
      loading,
      subscriptionsEmpty: !subscriptions || subscriptions.length === 0
    });
    
    // Проверим, корректно ли определяются типы данных
    if (subscriptions && subscriptions.length > 0) {
      const exampleSub = subscriptions[0];
      console.log('Пример подписки - тип interval:', typeof exampleSub.interval);
      console.log('Пример подписки - значение interval:', exampleSub.interval);
      console.log('Пример подписки - тип status:', typeof exampleSub.status);
      console.log('Пример подписки - значение status:', exampleSub.status);
    }
  }, [courierId, user, subscriptions, courierLoading, loading]);

  // Функция для возврата на рабочий стол
  const handleBack = () => {
    navigate('/courier');
  };

  if (!user) {
    return <AuthRequiredMessage />;
  }

  if (courierLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Мои подписки</h1>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (courierError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Мои подписки</h1>
        <ErrorDisplay errorMessage={`Ошибка при загрузке данных курьера: ${courierError}`} onRetry={handleRefresh} />
      </div>
    );
  }

  // Для отладки
  console.log('Страница подписок - данные:', {
    userId: user?.id,
    courierId,
    subscriptionsCount: subscriptions?.length,
    subscriptions
  });

  return (
    <div className="space-y-6">
      {/* Кнопка возврата на рабочий стол */}
      <div className="flex items-center mb-2">
        <Button 
          onClick={handleBack} 
          variant="outline"
          className="flex items-center gap-1 text-sm"
          size="sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Назад
        </Button>
      </div>
      
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Мои подписки</h1>
        <Button 
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Обновить</span>
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <SubscriptionsList 
        subscriptions={subscriptions} 
        onStatusChange={handleStatusChange} 
        actionLoading={actionLoading}
        loading={loading}
      />
      
      {/* Скрытый Dialog для исправления предупреждения доступности Radix UI */}
      <Dialog open={false}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Информация о подписке</DialogTitle>
            <DialogDescription>Детальная информация о вашей подписке на аренду</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
